import { useState } from "react";
import { Check, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal, { Eyebrow } from "./Reveal";
import { submitChecklist, checklistDownloadUrl } from "@/lib/api";

const PREVIEW = [
  "Claim your Google Business Profile",
  "Launch a searchable YouTube sermon archive",
  "Build a Plan Your Visit page that answers every guest question",
  "Create a 48-hour newcomer follow-up workflow",
];

export default function Checklist() {
  const [form, setForm] = useState({ name: "", email: "", church_name: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitChecklist(form);
      setDone(true);
      toast.success("Checklist unlocked — your download is starting.");
      window.open(checklistDownloadUrl, "_blank");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="checklist" data-testid="checklist-section"
      className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F3DF9F] via-[#E9CE7E] to-[#F3DF9F]">
      <div className="max-w-6xl mx-auto rounded-xl border border-gold/30 bg-navy-surface/70 backdrop-blur-xl p-8 sm:p-12 lg:p-16 grid lg:grid-cols-2 gap-12 items-center shadow-[0_0_60px_rgba(212,175,55,0.08)]">
        <Reveal>
          <Eyebrow>Free Lead Magnet</Eyebrow>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight text-white leading-[1.05]">
            The Church Digital Visibility Checklist
          </h2>
          <p className="mt-3 font-cormorant italic text-xl text-amber-100/90">
            21 things every church must have online.
          </p>
          <ul className="mt-8 space-y-3">
            {PREVIEW.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                <Check size={16} className="text-gold mt-0.5 shrink-0" />{p}
              </li>
            ))}
            <li className="text-xs text-slate-500 pl-7">…plus 17 more, in a printable one-page checklist.</li>
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          {done ? (
            <div className="text-center py-8" data-testid="checklist-form-success">
              <span className="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-gold text-navy mb-5">
                <Download size={24} />
              </span>
              <p className="font-display text-xl uppercase text-white">Checklist unlocked.</p>
              <p className="mt-3 text-sm text-slate-300">Your download has started. If it didn’t,
                <a data-testid="checklist-retry-download-link" href={checklistDownloadUrl}
                  className="text-gold underline underline-offset-4 ml-1">click here</a>.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="checklist-form">
              <input data-testid="checklist-name-input" className="field" placeholder="Your name" required
                value={form.name} onChange={set("name")} />
              <input data-testid="checklist-email-input" className="field" type="email" placeholder="Email address" required
                value={form.email} onChange={set("email")} />
              <input data-testid="checklist-church-input" className="field" placeholder="Church name (optional)"
                value={form.church_name} onChange={set("church_name")} />
              <button data-testid="lead-magnet-download-button" disabled={busy}
                className="w-full rounded-full bg-gold px-6 py-4 text-[11px] font-bold tracking-[0.18em] uppercase text-navy transition-all hover:bg-gold-hover hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                Send Me the Checklist
              </button>
              <p className="text-[11px] text-slate-500 text-center">No spam. Kingdom purposes only.</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
